import { Link, useLocation, useNavigate } from "react-router-dom";
/// Scroll
import { Dropdown } from "react-bootstrap";
import Logout from "./Logout";
import config from "../../../../config.json";
/// Image
import profileImg from "../../../assets/images/avatar/default-man.svg";
import { useEffect, useMemo, useState } from "react";
import { userProfile } from "../../../services/AuthService";
import {
  fetchUserNotifications,
  functionMarkAsRead,
} from "../../../services/NotificationsServices";
import socket from "../../../services/socket";
import "./notificationDropdown.css";
import "./floatingActions.css";
import "./headerLuxe.css";

import "../../../i18n";
import { persistChosenLanguage } from "../../../i18n";
import { useTranslation } from "react-i18next";
import { fetchRestoDataByUser } from "../../../services/restaurantService";
import { exportRestaurantFullZip } from "../../../services/restaurantExportAll";
import { toast } from "react-toastify";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import { isFranchisePortalSession } from "../../../utils/franchiseSession";
import { ONBOARDING_OPEN_EVENT } from "../../../utils/onboardingTutorialStorage";
import { getFranchiseMenuList, getMenuList } from "./Menu";

function normalizePath(pathname) {
  const clean = String(pathname || "/")
    .replace(/\/+$/, "")
    .toLowerCase();
  return clean || "/";
}

function pathLeaf(pathname) {
  const parts = normalizePath(pathname).split("/").filter(Boolean);
  return parts[parts.length - 1] || "";
}

function humanizeSlug(slug) {
  return String(slug || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const ENTITY_PAGE_ROUTES = [
  {
    prefix: "edit-product",
    parentKey: "navGroupKitchen",
    titleKey: "editProduct",
    titleFallback: "Edit Product",
  },
  {
    prefix: "product-sheet",
    parentKey: "navGroupKitchen",
    titleKey: "productDetails",
    titleFallback: "Product Details",
  },
  {
    prefix: "add-menu",
    parentKey: "navGroupKitchen",
    titleKey: "createMenu",
    titleFallback: "Create Menu",
  },
  {
    prefix: "edit-menu",
    parentKey: "navGroupKitchen",
    titleKey: "editMenu",
    titleFallback: "Edit Menu",
  },
  {
    prefix: "manage-menu",
    parentKey: "navGroupKitchen",
    titleKey: "manageMenu",
    titleFallback: "Manage Menu",
  },
  {
    prefix: "menu-details",
    parentKey: "navGroupKitchen",
    titleKey: "menuDetails",
    titleFallback: "Menu Details",
  },
  {
    prefix: "edit-subcategory",
    parentKey: "navGroupKitchen",
    titleKey: "editSubcategory",
    titleFallback: "Edit Subcategory",
  },
  {
    prefix: "subcategory-details",
    parentKey: "navGroupKitchen",
    titleKey: "subcategoryDetails",
    titleFallback: "Subcategory Details",
  },
  {
    prefix: "edit-category",
    parentKey: "navGroupKitchen",
    titleKey: "editCat",
    titleFallback: "Edit Category",
  },
  {
    prefix: "category-details",
    parentKey: "navGroupKitchen",
    titleKey: "categoryDetails",
    titleFallback: "Category Details",
  },
  {
    prefix: "edit-item",
    parentKey: "navGroupKitchen",
    titleKey: "editItem",
    titleFallback: "Edit Item",
  },
  {
    prefix: "item-details",
    parentKey: "navGroupKitchen",
    titleKey: "itemDetails",
    titleFallback: "Item Details",
  },
  {
    prefix: "edit-choice",
    parentKey: "navGroupKitchen",
    titleKey: "editChoice",
    titleFallback: "Edit Choice",
  },
  {
    prefix: "choice-details",
    parentKey: "navGroupKitchen",
    titleKey: "choiceDetails",
    titleFallback: "Choice Details",
  },
];

function resolveEntityPlace(pathname, t) {
  const full = normalizePath(pathname);
  for (const route of ENTITY_PAGE_ROUTES) {
    const marker = `/${route.prefix}/`;
    if (!full.startsWith(marker)) continue;
    const entityId = full.slice(marker.length).split("/")[0];
    if (!entityId) continue;
    return {
      parent: t(route.parentKey),
      title: t(route.titleKey, { defaultValue: route.titleFallback }),
    };
  }
  return null;
}

function resolveCurrentPlace(pathname, menuList, t) {
  const full = normalizePath(pathname);
  const leaf = pathLeaf(pathname);

  if (!leaf) {
    return { parent: null, title: t("navHome") || t("dashboard") || "Home" };
  }

  const entityPlace = resolveEntityPlace(pathname, t);
  if (entityPlace) return entityPlace;

  let best = null;
  for (const item of menuList) {
    if (item.to) {
      const target = normalizePath(`/${item.to}`);
      if (full === target || full.startsWith(`${target}/`)) {
        const score = target.length;
        if (!best || score > best.score) {
          best = { parent: null, title: item.title, score };
        }
      }
    }
    if (Array.isArray(item.content)) {
      for (const child of item.content) {
        if (!child.to) continue;
        const target = normalizePath(`/${child.to}`);
        if (full === target || full.startsWith(`${target}/`)) {
          const score = target.length;
          if (!best || score > best.score) {
            best = { parent: item.title, title: child.title, score };
          }
        }
      }
    }
  }

  if (best) {
    return { parent: best.parent, title: best.title };
  }

  const EXTRA = {
    notifications: t("notifications"),
    "app-profile": t("profile") || t("navRestaurantInfo"),
    "franchise-profile": t("franchiseNavProfile") || t("profile"),
    "franchise-restaurants": t("franchiseNavOverview"),
    "start-now": t("startNow") || "Start now",
    "stripe-account": t("accessStripe") || "Stripe",
  };

  if (EXTRA[leaf]) {
    return { parent: null, title: EXTRA[leaf] };
  }

  return { parent: null, title: humanizeSlug(leaf) };
}

// eslint-disable-next-line react/prop-types
const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLanguage = i18n.language;
  const langCode = String(currentLanguage || "en").split("-")[0].toLowerCase();
  const menuLanguages = useMemo(() => getMenuLanguages(t), [t]);
  const currentLangEntry =
    menuLanguages.find((l) => l.code === langCode) || menuLanguages[0];
  const navigate = useNavigate();
  const franchisePortal = isFranchisePortalSession();

  const menuList = useMemo(
    () => (franchisePortal ? getFranchiseMenuList(t) : getMenuList(t)),
    [franchisePortal, t]
  );

  const currentPlace = useMemo(
    () => resolveCurrentPlace(location.pathname, menuList, t),
    [location.pathname, menuList, t]
  );

  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openRestaurantUrl, setOpenRestaurantUrl] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const profilePath = franchisePortal ? "/franchise-profile" : "/app-profile";
  const avatarSrc =
    user?.image && !avatarFailed
      ? `${config.UPLOADS_SERVER}/user/${user.image}`
      : profileImg;

  useEffect(() => {
    setAvatarFailed(false);
  }, [user?.image]);

  const closeFab = () => setFabOpen(false);

  const toggleFab = () => setFabOpen((open) => !open);

  useEffect(() => {
    const manual = localStorage.getItem("currentLanguageManual") === "1";
    const savedLanguage = localStorage.getItem("currentLanguage");
    if (manual && savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    persistChosenLanguage(lng);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await userProfile();
        setUser(userData);

        // On récupère les notifications après avoir récupéré l'utilisateur
        // L'API renvoie { notifications, unreadCount }
        const data = await fetchUserNotifications();
        const notifs = Array.isArray(data?.notifications)
          ? data.notifications
          : [];
        setNotifications(notifs);
        setUnreadCount(
          typeof data?.unreadCount === "number"
            ? data.unreadCount
            : notifs.filter((notif) => notif.status === "unread").length
        );
      } catch (error) {
        console.log(error);
      }
    };
    fetchUserData();
  }, []);

  // Écoute en temps réel des nouvelles réservations d'événement et réponses support
  useEffect(() => {
    const handleNewEventReservation = (notif) => {
      if (!notif) return;
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    const handleNewTableReservation = (notif) => {
      if (!notif) return;
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    const handleNewSupportReply = (notif) => {
      if (!notif || !user?._id) return;
      if (String(notif.userConcerned) !== String(user._id)) return;
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    const handleNewClientReclamation = (notif) => {
      if (!notif) return;
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    socket.on("new-event-reservation", handleNewEventReservation);
    socket.on("new-table-reservation", handleNewTableReservation);
    socket.on("new-support-reply", handleNewSupportReply);
    socket.on("new-client-reclamation", handleNewClientReclamation);
    return () => {
      socket.off("new-event-reservation", handleNewEventReservation);
      socket.off("new-table-reservation", handleNewTableReservation);
      socket.off("new-support-reply", handleNewSupportReply);
      socket.off("new-client-reclamation", handleNewClientReclamation);
    };
  }, [user?._id]);

  useEffect(() => {
    if (franchisePortal) return undefined;

    const loadOpenRestaurantUrl = async () => {
      try {
        const data = await fetchRestoDataByUser();
        const resto = data?.restaurant ?? data;
        if (!resto?.country || !resto?.city || !resto?.nameRes) return;

        const cleanCountry = resto.country.toLowerCase().replace(/\s+/g, "-");
        const cleanCity = resto.city.toLowerCase().replace(/\s+/g, "-");
        const cleanName = resto.nameRes.toLowerCase().replace(/\s+/g, "-");

        setOpenRestaurantUrl(
          `${config.client}/restaurant/${cleanCountry}/${cleanCity}/${cleanName}`
        );
      } catch (error) {
        console.error("Error building open restaurant link:", error);
      }
    };

    loadOpenRestaurantUrl();
  }, [franchisePortal]);

  const handleExportAll = async () => {
    if (exportingAll) return;
    setExportingAll(true);
    toast.info(t("exportAllRunning"));
    try {
      await exportRestaurantFullZip({ userImage: user?.image || undefined });
      toast.success(t("exportAllSuccess"));
    } catch (e) {
      console.error(e);
      toast.error(t("exportAllFailed"));
    } finally {
      setExportingAll(false);
    }
  };

  const handleNotificationClick = async (notifId) => {
    try {
      const clicked = notifications.find((n) => n._id === notifId);
      const wasUnread = clicked?.status === "unread";

      // Appel à l'API pour marquer comme lu
      await functionMarkAsRead(notifId);

      // Mettre à jour le state local
      setNotifications((prev) =>
        prev.map((n) => (n._id === notifId ? { ...n, status: "read" } : n))
      );
      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      // Redirection en fonction du type de notification
      const eventId = clicked?.extraData?.eventId;
      const supportId = clicked?.extraData?.supportId;
      if (clicked?.type === "event_reservation" && eventId) {
        navigate(`/events/${eventId}/reservations`);
      } else if (clicked?.type === "table_reservation") {
        navigate("/reservations");
      } else if (clicked?.type === "client_reclamation") {
        navigate("/client-reclamations");
      } else if (clicked?.type === "support_reply" && supportId) {
        navigate(`/my-support-requests/${supportId}`);
      } else if (clicked?.type === "order") {
        navigate("/orders");
      } else {
        navigate("/orders");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la notification:", error);
    }
  };

  const handleMarkAllAsRead = async (e) => {
    e?.stopPropagation();
    const unread = notifications.filter((n) => n.status === "unread");
    if (unread.length === 0) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, status: "read" })));
    setUnreadCount(0);
    try {
      await Promise.all(unread.map((n) => functionMarkAsRead(n._id)));
    } catch (error) {
      console.error("Erreur lors du marquage global des notifications:", error);
    }
  };

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);
    const diffMs = Date.now() - date.getTime();
    if (Number.isNaN(diffMs)) return "";
    const sec = Math.round(diffMs / 1000);
    const min = Math.round(sec / 60);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);
    if (sec < 60) return t("timeJustNow");
    if (min < 60) return t("timeMinutesAgo", { count: min });
    if (hr < 24) return t("timeHoursAgo", { count: hr });
    if (day < 7) return t("timeDaysAgo", { count: day });
    return date.toLocaleDateString();
  };

  const renderNotifIcon = (type) => {
    if (type === "event_reservation") {
      return (
        <span className="c2e-notif__icon c2e-notif__icon--event">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={19}
            height={19}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
      );
    }
    if (type === "table_reservation") {
      return (
        <span className="c2e-notif__icon c2e-notif__icon--table">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={19}
            height={19}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 10h16" />
            <path d="M6 10V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3" />
            <path d="M8 10v9" />
            <path d="M16 10v9" />
          </svg>
        </span>
      );
    }
    if (type === "order") {
      return (
        <span className="c2e-notif__icon c2e-notif__icon--order">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={19}
            height={19}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
        </span>
      );
    }
    if (type === "support_reply") {
      return (
        <span className="c2e-notif__icon c2e-notif__icon--support">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={19}
            height={19}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </span>
      );
    }
    return (
      <span className="c2e-notif__icon c2e-notif__icon--default">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={19}
          height={19}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </span>
    );
  };

  return (
    <>
      <div className="header">
        <div className="header-content">
          <nav className="navbar navbar-expand">
            <div className="collapse navbar-collapse justify-content-between">
              <div className="header-left">
                <div className="c2e-header-place" aria-live="polite">
                  {franchisePortal ? (
                    <span className="c2e-header-place__workspace">
                      {t("franchisePortalTitle")}
                    </span>
                  ) : null}
                  {currentPlace.parent ? (
                    <span className="c2e-header-place__crumb">
                      <span className="c2e-header-place__parent">
                        {currentPlace.parent}
                      </span>
                      <span className="c2e-header-place__sep" aria-hidden>
                        /
                      </span>
                      <span className="c2e-header-place__title">
                        {currentPlace.title}
                      </span>
                    </span>
                  ) : (
                    <span className="c2e-header-place__title">
                      {currentPlace.title}
                    </span>
                  )}
                </div>
              </div>
            <ul className="navbar-nav header-right">
              <Dropdown className="nav-item dropdown language_dropdown" as="li">
                <Dropdown.Toggle
                  as="a"
                  variant=""
                  className="nav-link ai-icon i-false c2e-nav-chip"
                  href="#"
                  role="button"
                  data-toggle="dropdown"
                >
                  <img
                    src={currentLangEntry?.flag}
                    alt=""
                    className="c2e-nav-chip__flag"
                  />
                  <span className="c2e-nav-chip__label">
                    {currentLangEntry?.label}
                  </span>
                  <svg
                    className="c2e-nav-chip__chevron"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  {menuLanguages.map((lang) => (
                    <Dropdown.Item
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      active={lang.code === langCode}
                    >
                      <img src={lang.flag} alt="" />
                      {lang.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="nav-item dropdown c2e-notif" as="li">
                <Dropdown.Toggle
                  as="a"
                  variant=""
                  className="nav-link i-false c2e-notif__toggle c2e-nav-chip c2e-nav-chip--icon"
                  href="#"
                  role="button"
                  data-toggle="dropdown"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={21}
                    height={21}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="c2e-notif__badge c2e-notif__badge--pulse">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu align="end" className="mt-2 c2e-notif__menu">
                  <div className="c2e-notif__header">
                    <span className="c2e-notif__header-title">
                      {t("notifications")}
                      {unreadCount > 0 && (
                        <span className="c2e-notif__header-count">
                          {unreadCount}
                        </span>
                      )}
                    </span>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        className="c2e-notif__markall"
                        onClick={handleMarkAllAsRead}
                      >
                        {t("markAllAsRead")}
                      </button>
                    )}
                  </div>

                  <div className="c2e-notif__list">
                    {notifications.length === 0 ? (
                      <div className="c2e-notif__empty">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width={40}
                          height={40}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.6}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        <span className="c2e-notif__empty-text">
                          {t("noNotifications")}
                        </span>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <Dropdown.Item
                          key={notif._id}
                          onClick={() => handleNotificationClick(notif._id)}
                          className={`c2e-notif__item${
                            notif.status === "unread"
                              ? " c2e-notif__item--unread"
                              : ""
                          }`}
                        >
                          {renderNotifIcon(notif.type)}
                          <div className="c2e-notif__body">
                            <p className="c2e-notif__title">{notif.title}</p>
                            <p className="c2e-notif__text">{notif.text}</p>
                            {notif.createdAt && (
                              <span className="c2e-notif__time">
                                {formatRelativeTime(notif.createdAt)}
                              </span>
                            )}
                          </div>
                          {notif.status === "unread" && (
                            <span className="c2e-notif__dot" aria-hidden />
                          )}
                        </Dropdown.Item>
                      ))
                    )}
                  </div>

                  <button
                    type="button"
                    className="c2e-notif__footer"
                    onClick={() => navigate("/notifications")}
                  >
                    {t("viewAllNotifications")}
                  </button>
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown className="nav-item dropdown header-profile" as="li">
                <Dropdown.Toggle
                  as="a"
                  to="#"
                  variant=""
                  className="nav-link i-false p-0c-pointer pointr c2e-nav-profile"
                >
                  <img
                    src={avatarSrc}
                    width={34}
                    height={34}
                    alt=""
                    className="c2e-nav-profile__avatar"
                    onError={() => setAvatarFailed(true)}
                  />

                  <div className="header-info c2e-nav-profile__meta">
                    <span className="c2e-nav-profile__name">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <p className="fs-12 mb-0 c2e-nav-profile__role">
                      {user?.role === "superAdmin"
                        ? t("roleSuperAdmin")
                        : user?.role === "resFranchise"
                          ? t("franchiseManagerRole")
                          : t("roleAdmin")}
                    </p>
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu align="end">
                  <Link to={profilePath} className="dropdown-item ai-icon">
                    <svg
                      id="icon-user1"
                      xmlns="http://www.w3.org/2000/svg"
                      width={18}
                      height={18}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx={12} cy={7} r={4} />
                    </svg>
                    <span>{t("profile")}</span>
                  </Link>

                  {!franchisePortal ? (
                    <Dropdown.Item
                      as="button"
                      type="button"
                      disabled={exportingAll}
                      className="dropdown-item ai-icon d-flex align-items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        handleExportAll();
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span>
                        {exportingAll ? t("exporting") : t("exportAll")}
                      </span>
                    </Dropdown.Item>
                  ) : null}

                  <Logout />
                </Dropdown.Menu>
              </Dropdown>
            </ul>
            </div>
          </nav>
        </div>
      </div>

      <div className={`fl-fab${fabOpen ? " is-open" : ""}`}>
        {fabOpen && (
          <button
            type="button"
            className="fl-fab__backdrop"
            aria-label={t("fabClose")}
            onClick={closeFab}
          />
        )}

        <div className="fl-fab__toggle-wrap">
          <button
            type="button"
            className={`fl-fab__toggle${fabOpen ? " is-open" : ""}`}
            aria-expanded={fabOpen}
            aria-label={fabOpen ? t("fabCloseQuickActions") : t("fabOpenQuickActions")}
            data-tour="tour-nav-setup-guide"
            onClick={toggleFab}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.2}
              strokeLinecap="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        <div className="fl-fab__actions" aria-hidden={!fabOpen}>
          {openRestaurantUrl ? (
            <a
              href={openRestaurantUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="fl-fab__action"
              title={t("viewAsClient")}
              onClick={closeFab}
            >
              <span className="fl-fab__action-btn" aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </span>
              <span className="fl-fab__action-label">{t("viewAsClient")}</span>
            </a>
          ) : null}

          {!franchisePortal ? (
            <button
              type="button"
              className="fl-fab__action"
              onClick={() => {
                closeFab();
                window.dispatchEvent(new CustomEvent(ONBOARDING_OPEN_EVENT));
              }}
            >
              <span className="fl-fab__action-btn" aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                  <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                  <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                  <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
                </svg>
              </span>
              <span className="fl-fab__action-label">
                {t("onboardingFabLabel")}
              </span>
            </button>
          ) : null}

          {!franchisePortal ? (
            <button
              type="button"
              className="fl-fab__action"
              onClick={() => {
                closeFab();
                window.dispatchEvent(new CustomEvent("open-support-modal"));
              }}
            >
              <span className="fl-fab__action-btn" aria-hidden>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" />
                  <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" />
                </svg>
              </span>
              <span className="fl-fab__action-label">
                {t("navCustomerSupport") || "Support"}
              </span>
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default Header;
